import React, { memo, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'
import {
  Button,
  FormModal,
  FormModalActions,
  FormModalContent,
  FormModalTitle,
  Input,
} from 'sputnik-ui'
import { showErrorMessage, showInfoMessage } from 'store/ui/actions'
import {
  SERVICE_NOTE_MAX_LENGTH,
  updateServiceNote,
} from '../model/updateServiceNote'

// device — элемент списка устройств: uuid, type ('camera' | 'intercom') и alias.
export const DeviceServiceNoteModal = memo(function DeviceServiceNoteModal({
  open,
  device,
  onClose,
}) {
  const { t } = useTranslation(['devices', 'common'])
  const dispatch = useDispatch()
  const [serviceNote, setServiceNote] = useState('')
  const [pending, setPending] = useState(false)
  const initialNote = device?.alias || ''
  // maxLength не обрезает значение, пришедшее из списка, а ГИС длиннее не примет
  const tooLong = serviceNote.trim().length > SERVICE_NOTE_MAX_LENGTH

  // Модалка смонтирована постоянно: без сброса при открытии в поле остался бы
  // несохранённый текст прошлого раза.
  useEffect(() => {
    if (open) setServiceNote(initialNote)
  }, [open, initialNote])

  const handleSave = () => {
    if (!device || pending) return

    setPending(true)
    dispatch(
      updateServiceNote({
        deviceUuid: device.uuid,
        type: device.type,
        serviceNote: serviceNote.trim() || null,
      })
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(showInfoMessage(t('devices:service_note.saved')))
        onClose()
      })
      .catch((error) => {
        dispatch(
          showErrorMessage(
            typeof error === 'string' && error
              ? error
              : t('devices:service_note.save_error')
          )
        )
      })
      .finally(() => setPending(false))
  }

  // Enter сохраняет и не уходит дальше: на главной окно слушает Enter и открывает
  // первое устройство списка. Вешаем на <input> через inputProps — Input из
  // sputnik-ui раскладывает свои props и на обёртку, и обработчик сработал бы дважды.
  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    event.stopPropagation()
    if (!tooLong) handleSave()
  }

  return (
    <FormModal open={open} onClose={onClose}>
      <FormModalTitle>{t('devices:service_note.title')}</FormModalTitle>
      <FormModalContent>
        <Input
          name="service_note"
          value={serviceNote}
          error={tooLong}
          onChange={(event) => setServiceNote(event.target.value)}
          label={t('devices:cameras.third_party.alias')}
          fullWidth
          disabled={pending}
          inputProps={{
            maxLength: SERVICE_NOTE_MAX_LENGTH,
            onKeyDown: handleKeyDown,
          }}
        />
      </FormModalContent>
      <FormModalActions>
        <Button
          variant="v2"
          primary
          fullWidth
          onClick={handleSave}
          disabled={pending || tooLong}
        >
          {t('common:button.save_edit')}
        </Button>
        <Button fullWidth variant="v2" tertiary onClick={onClose}>
          {t('common:button.back')}
        </Button>
      </FormModalActions>
    </FormModal>
  )
})
